import NeptuneElement from '../../NeptuneElement.js';
import { i18nMixin } from '../../../utils/i18nMixin.js';
import { i18n } from '../../../utils/i18n.js';

class ProfileMenu extends i18nMixin(NeptuneElement) {
  templateMarkup = `
        <slot name="trigger"></slot>
        <div role="dialog" aria-label="Profile menu" hidden>
            <header>
                <neptune-avatar id="profileAvatar" size="md"></neptune-avatar>
                <div>
                    <span id="profileName"></span>
                    <span id="profileEmail" hidden></span>
                </div>
            </header>
            <neptune-menu id="profileMenuList" role="menu"></neptune-menu>
        </div>
    `;
  componentCssUrl = '/src/components/chrome/profileMenu/profile-menu.css';

  #outsideClickHandler = null;
  #keydownHandler = null;
  #menuSelectHandler = null;
  #menuNavigateHandler = null;
  #anchorElement = null;
  #previouslyFocused = null;
  #kebabToggleHandler = null;
  #headerMenuOpenedHandler = null;
  #slottedTriggerHandler = null;
  #headerElement = null;
  #profileTriggerHandler = null;
  #kebabNavigateHandler = null;

  constructor() {
    super();
    this.userInfo = { name: '', email: '', initials: '' };
    this.menuItems = [];
  }

  async connectedCallback() {
    await super.connectedCallback();
    const culture = this.getAttribute('culture');
    if (culture) {
      i18n.setLocale(culture);
    }
    this.setEventListeners();
    this.#setupSlottedTrigger();

    const itemsAttr = this.getAttribute('items');
    if (itemsAttr) {
      try {
        const parsed = JSON.parse(itemsAttr);
        if (Array.isArray(parsed)) { this.menuItems = parsed; }
      } catch { /* ignore */ }
    }

    const userInfoAttr = this.getAttribute('user-info');
    if (userInfoAttr) {
      try {
        const info = JSON.parse(userInfoAttr);
        this.userInfo = {
          name: info.userName ?? this.userInfo.name,
          email: info.userEmail ?? this.userInfo.email,
          initials: info.userInitials ?? this.userInfo.initials,
        };
      } catch { /* ignore */ }
    }

    await this.#autoWireHeader();
    this.#renderUserInfo();
    this.#renderMenuItems();
    if (this.hasAttribute('open')) {
      this.#openMenu();
    }
  }

  disconnectedCallback() {
    this.#unwireHeader();
    this.removeEventListeners();
    super.disconnectedCallback?.();
  }

  static get observedAttributes() {
    return ['open', 'for', 'items', 'user-info', 'culture'];
  }


  attributeChangedCallback(name, oldValue, newValue) {
    if (!this.isRendered) {
      return;
    }
    if (name === 'culture' && newValue) {
      i18n.setLocale(newValue);
      return;
    }

    if (name === 'open') {
      this.hasAttribute('open') ? this.#openMenu() : this.#closeMenu();
    } else if (name === 'items') {
      try {
        const parsed = JSON.parse(newValue);
        if (Array.isArray(parsed)) {
          this.menuItems = parsed;
          this.#renderMenuItems();
          this.#syncKebabMenuItems();
        }
      } catch { /* invalid JSON, ignore */ }
    } else if (name === 'user-info') {
      try {
        const info = JSON.parse(newValue);
        this.userInfo = {
          name: info.userName ?? this.userInfo.name,
          email: info.userEmail ?? this.userInfo.email,
          initials: info.userInitials ?? this.userInfo.initials,
        };
        this.#renderUserInfo();
        this.#syncHeaderAvatar();
        this.#syncKebabMenuItems();
      } catch { /* invalid JSON, ignore */ }
    }
  }

  onLocaleChange() {
    this.#applyTranslations();
  }

  #applyTranslations() {
    const dialog = this.shadowRoot?.querySelector('div[role="dialog"]');

    if (dialog) {
        dialog.setAttribute('aria-label', this.translate('PROFILE_MENU.ARIA_LABEL'));
    }
    this.#renderMenuItems();
    this.#syncKebabMenuItems();
  }

  /**
   * Pushes the current initials into the header so its avatar shows them
   * without needing a separate `user-initials` attribute.
   */
  #syncHeaderAvatar() {
    if (!this.#headerElement) {
      return;
    }
    const avatar = this.#headerElement.shadowRoot?.querySelector('#profileAvatar');
    if (avatar) {
      avatar.setAttribute('initials', this.userInfo.initials || '');
    }
  }

  /**
   * opens the menu, optionally anchored to a trigger element.
   * @param {HTMLElement} [anchorElement]
   * @returns {void}
   */
  open(anchorElement) {
    this.#anchorElement = anchorElement || this.#anchorElement;
    this.setAttribute('open', '');
  }

  close() {
    this.removeAttribute('open');
  }

  /**
   * toggles the menu open/close.
   * @param {HTMLElement} [anchorElement]
   * @returns {void}
   */
  toggle(anchorElement) {
    this.hasAttribute('open') ? this.close() : this.open(anchorElement);
  }

  setEventListeners() {
    this.removeEventListeners();
    const menuList = this.shadowRoot?.querySelector('#profileMenuList');
    if (!menuList) {
      return;
    }

    this.#menuSelectHandler = (event) => this.#handleMenuItemSelect(event);
    this.#menuNavigateHandler = (event) => this.#handleMenuNavigate(event);
    menuList.addEventListener('menu-item-select', this.#menuSelectHandler);
    menuList.addEventListener('neptune-menu-navigate', this.#menuNavigateHandler);
  }

  removeEventListeners() {
    const menuList = this.shadowRoot?.querySelector('#profileMenuList');
    if (menuList) {
      if (this.#menuSelectHandler) { 
        menuList.removeEventListener('menu-item-select', this.#menuSelectHandler);
      }
      if (this.#menuNavigateHandler) {
        menuList.removeEventListener('neptune-menu-navigate', this.#menuNavigateHandler);
      }
    }
    this.#menuSelectHandler = null;
    this.#menuNavigateHandler = null;
    this.#removeGlobalListeners();
  }

  /**
   * Wires up any element placed in slot="trigger" so clicking it toggles the menu.
   * @returns {void}
   */
  #setupSlottedTrigger() {
    const slot = this.shadowRoot?.querySelector('slot[name="trigger"]');
    if (!slot) {
      return;
    }

    const bind = () => {
      const trigger = slot.assignedElements({ flatten: true })[0];
      if (!trigger) {
        return;
      }
      if (this.#slottedTriggerHandler) {
        trigger.removeEventListener('click', this.#slottedTriggerHandler);
      }
      this.#slottedTriggerHandler = (event) => {
        event.stopPropagation();
        this.toggle(trigger);
      };
      trigger.addEventListener('click', this.#slottedTriggerHandler);
    };

    slot.addEventListener('slotchange', bind);
    bind();
  }

  /**
  * Renders the avatar and user identity block (name, email, initials)
  * inside the shadow DOM. Waits for neptune-avatar to be defined so the
  * initials attribute is applied reliably.
  * @returns {Promise<void>} Resolves once the avatar has been updated.
  */
  async #renderUserInfo() {
    if (!this.isRendered) {
      return;
    }
    const avatar = this.shadowRoot?.querySelector('#profileAvatar');
    const nameEl = this.shadowRoot?.querySelector('#profileName');
    const emailEl = this.shadowRoot?.querySelector('#profileEmail');

    if (nameEl) {
      nameEl.textContent = this.userInfo.name || '';
    }
    if (emailEl) {
      emailEl.textContent = this.userInfo.email || '';
      emailEl.hidden = !this.userInfo.email;
    }

    if (avatar) {
      await customElements.whenDefined('neptune-avatar');
      avatar.removeAttribute('initials');
      if (this.userInfo.initials) {
        avatar.setAttribute('initials', this.userInfo.initials);
      }
    }
  }

  /**
   * Dispatches profile-menu-select.
   * Shared by desktop dropdown and mobile kebab menu.
   * @param {object} selected - The selected menu item config.
   * @returns {void}
   */
  #emitSelect(selected) {
    this.dispatchEvent(
      new CustomEvent('profile-menu-select', {
        bubbles: true,
        composed: true,
        detail: { ...selected },
      }),
    );
    if (this.#headerElement) {
      this.#headerElement.dispatchEvent(
        new CustomEvent('header-item-selected', {
          bubbles: true,
          composed: true,
          detail: { item: selected },
        }),
      );
    }
  }

  /**
  * Renders the configurable menu items inside neptune-menu. Each item is
  * projected into the `menu-item` slot and carries `data-id` for lookup.
  * @returns {void}
  */
  #renderMenuItems() {
    if (!this.isRendered) {
      return;
    }
    const menuList = this.shadowRoot?.querySelector('#profileMenuList');
    if (!menuList) {
      return;
    }
    menuList.innerHTML = '';

    this.menuItems.forEach((item) => {
      const menuItem = document.createElement('neptune-menu-item');
      menuItem.setAttribute('slot', 'menu-item');
      menuItem.dataset.id = item.id;
      if (item.divider) {
        menuItem.setAttribute('divider', '');
      }
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'profile-menu-item-button';
      button.setAttribute('aria-label', item.name);

      if (item.icon) {
        const icon = document.createElement('i');
        icon.className = item.icon;
        icon.setAttribute('aria-hidden', 'true');
        button.appendChild(icon);
      }
      button.append(item.name);
      menuItem.appendChild(button);
      menuList.appendChild(menuItem);
    });
  }

  /**
   * Handles menu item selection and emits profile-menu-select.
   * @param {CustomEvent} event
   * @returns {void}
   */
  #handleMenuItemSelect(event) {
    const id = event.detail?.item?.dataset?.id;
    const selected = this.menuItems.find(
      (menuItem) => String(menuItem.id) === String(id),
    );
    if (!selected) {
      return;
    }
    this.#emitSelect(selected);
    this.close();
    if (selected.route) {
      window.dispatchEvent(
      new CustomEvent('neptune-location-change', {
        detail: { route: selected.route }
      }));
    }

  }

  /**
   * Handles keyboard navigation events bubbled from menu items.
   * @param {CustomEvent} event
   * @returns {void}
   */
  #handleMenuNavigate(event) {
    const { key, sourceItem } = event.detail || {};
    if (key === 'Escape') {
      this.close();
      return;
    }
    const items = Array.from(
      this.shadowRoot?.querySelectorAll('neptune-menu-item:not([disabled])') ||
        [],
    );
    if (!items.length) {
      return;
    }
    const currentIndex = items.indexOf(sourceItem);
    let nextIndex = currentIndex;

    if (key === 'ArrowDown' || key === 'Down') {
      nextIndex = (currentIndex + 1) % items.length;
    } else if (key === 'ArrowUp' || key === 'Up') {
      nextIndex = (currentIndex - 1 + items.length) % items.length;
    } else if (key === 'Home') {
      nextIndex = 0;
    } else if (key === 'End') {
      nextIndex = items.length - 1;
    }

    items[nextIndex]?.focus();
  }

  /**
  * Opens the menu — unhides the dialog, closes other header dropdowns,
  * positions the popover relative to the anchor, focuses the first item,
  * attaches global listeners, and dispatches `profile-menu-open`.
  * @returns {void}
  */
  #openMenu() {
    const container = this.shadowRoot?.querySelector('div[role="dialog"]');
    if (!container) {
      return;
    }

    this.#headerElement?.closeAllMenus?.();

    const kebab = document.querySelector('neptune-kebab-menu');
    if (kebab?.isOpen?.()) {
      kebab.close();
    }

    container.hidden = false;

    this.#previouslyFocused = document.activeElement;
    this.#positionMenu();
    this.#attachGlobalListeners();

    requestAnimationFrame(() => {
      this.shadowRoot
        ?.querySelectorAll('neptune-menu-item[selected]')
        .forEach(item => item.removeAttribute('selected'));
    });

    const firstItem = this.shadowRoot?.querySelector(
      'neptune-menu-item:not([disabled])',
    );
    firstItem?.focus?.();

    this.dispatchEvent(
      new CustomEvent('profile-menu-open', { bubbles: true, composed: true }),
    );
  }

  /**
   * Closes the menu, restores focus, and removes global listeners.
   * @returns {void}
   */
  #closeMenu() {
    const container = this.shadowRoot?.querySelector('div[role="dialog"]');
    if (!container) {
      return;
    }
    container.hidden = true;
    this.#removeGlobalListeners();

    if (
      this.#previouslyFocused &&
      typeof this.#previouslyFocused.focus === 'function'
    ) {
      this.#previouslyFocused.focus();
    }
    this.#previouslyFocused = null;

    this.dispatchEvent(
      new CustomEvent('profile-menu-close', {
        bubbles: true,
        composed: true,
      }),
    );
  }

  /**
   * Positions the menu container relative to the anchor (profile icon) if
   * one is provided. Falls back to CSS-driven positioning otherwise.
   * @returns {void}
  */
  #positionMenu() {
    if (!this.#anchorElement) {
      return;
    }
    const container = this.shadowRoot?.querySelector('div[role="dialog"]');
      if (!container) {
        return;
      }

      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;
      const triggerRect = this.#anchorElement.getBoundingClientRect();
      const menuWidth = container.offsetWidth || 280;
      const menuMaxHeight = 400;
      const gap = 8;

      let top = triggerRect.bottom - gap;
      const availableHeightBelow = viewportHeight - top;
      if (availableHeightBelow < 100 && triggerRect.top > 200) {
          top = triggerRect.top - Math.min(menuMaxHeight, 200) - gap;
      }

      let left;
      if (triggerRect.right > viewportWidth * 0.75) {
          left = triggerRect.right - menuWidth;
      } else if (triggerRect.left < viewportWidth * 0.25) {
          left = triggerRect.left;
      } else {
          left = triggerRect.left + (triggerRect.width / 2) - (menuWidth / 2);
      }

      if (left < gap) {
          left = gap;
      } else if (left + menuWidth > viewportWidth - gap) {
          left = viewportWidth - menuWidth - gap;
      }

      container.style.setProperty('--menu-top', `${Math.max(gap, top)}px`);
      container.style.setProperty('--menu-left', `${left}px`);
  }

  /**
  * Wires the component to a neptune-header identified by the `for`
  * attribute. Attaches click and menu-coordination handlers on the profile
  * icon, and bridges mobile kebab menu selections.
  * @returns {Promise<void>} Resolves once wiring completes.
  */
  async #autoWireHeader() {
    const forId = this.getAttribute('for');
    if (!forId) {
      return;
    }

    await customElements.whenDefined('neptune-header');
    this.#headerElement = document.getElementById(forId);
    if (!this.#headerElement) {
      return;
    }

    const profileActionItem = await this.#waitForProfileActionItem();
    if (!profileActionItem) {
      return;
    }

    this.#wireHeaderCoordination(profileActionItem);
    this.#wireKebabMenu();
  }

  /**
   * Intercepts the header's profile icon click and closes sibling dropdowns.
   * @param {HTMLElement} profileActionItem - The header's profile icon element.
   * @returns {void}
   */
  #wireHeaderCoordination(profileActionItem) {
    this.#headerMenuOpenedHandler = (event) => {
      const opened = event.composedPath().find((el) => el?.tagName?.toLowerCase() === 'neptune-header-action-item');
      if (opened && opened.id !== 'profileActionItem') {
        this.close();
      }
    };
    this.#headerElement.addEventListener(
      'menu-opened',
      this.#headerMenuOpenedHandler,
      true,
    );

    this.#profileTriggerHandler = (event) => {
      event.stopImmediatePropagation();
      event.preventDefault();
      profileActionItem.closeMenu?.();
      this.toggle(profileActionItem);
    };
    profileActionItem.addEventListener(
      'click',
      this.#profileTriggerHandler,
      true,
    );
  }

  /**
   * Bridges kebab item clicks to profile-menu-select so consumers handle
   * one event on both mobile and desktop.
   */
  async #wireKebabMenu() {
    await customElements.whenDefined('neptune-kebab-menu');
    const kebab = document.querySelector('neptune-kebab-menu');
    if (!kebab) {
      return;
    }

    this.#kebabToggleHandler = (event) => {
      if (event.detail?.state) {
        this.close();
      }
    };
    kebab.addEventListener('neptune-kebab-menu-toggle', this.#kebabToggleHandler);

    this.#kebabNavigateHandler = (event) => {
      const item = event.detail?.item || event.detail;
      if (!item) {
        return;
      }
      const selected = this.menuItems.find(
          (menuItem) =>
              String(menuItem.id) === String(item.id) && menuItem.name === item.name,
      );
      if (!selected) {
        return;
      }
      this.#emitSelect(selected);
    };
    kebab.addEventListener('neptune-kebab-menu-navigate', this.#kebabNavigateHandler);
  }
  /**
   * Removes header wiring on disconnect.
   * @returns {void}
   */
  #unwireHeader() {
    const kebabForNavigate = document.querySelector('neptune-kebab-menu');
    if (kebabForNavigate && this.#kebabNavigateHandler) {
      kebabForNavigate.removeEventListener(
        'neptune-kebab-menu-navigate',
        this.#kebabNavigateHandler,
      );
    }
    const profileActionItem = this.#headerElement?.shadowRoot?.querySelector('#profileActionItem');
    if (profileActionItem && this.#profileTriggerHandler) {
      profileActionItem.removeEventListener(
        'click',
        this.#profileTriggerHandler,
        true,
      );
    }
    this.#headerElement = null;
    this.#profileTriggerHandler = null;
    this.#kebabNavigateHandler = null;

    const kebab = document.querySelector('neptune-kebab-menu');
    if (kebab && this.#kebabToggleHandler) {
      kebab.removeEventListener(
        'neptune-kebab-menu-toggle',
        this.#kebabToggleHandler,
      );
    }
    this.#kebabToggleHandler = null;

    if (this.#headerElement && this.#headerMenuOpenedHandler) {
      this.#headerElement.removeEventListener(
        'menu-opened',
        this.#headerMenuOpenedHandler,
        true,
      );
    }
    this.#headerMenuOpenedHandler = null;
  }

  /**
   * Waits until the header's profile action item is available in its shadow root.
   * @returns {Promise<HTMLElement|null>}
   */
  async #waitForProfileActionItem() {
    for (let i = 0; i < 20; i++) {
      const element =
        this.#headerElement?.shadowRoot?.querySelector('#profileActionItem');
      if (element) {
        return element;
      }
      await new Promise((resolve) => {
        setTimeout(resolve, 50);
      });
    }
    return null;
  }

  /**
   * Mirrors the profile menu items into the header's headerActionItems.profile
   * so the kebab menu (mobile view) shows the same list under the Profile section.
   * @returns {void}
   */
  #syncKebabMenuItems() {
    if (!this.#headerElement || !this.menuItems.length) {
      return;
    }

    if (!this.#headerElement.profileLabelPatched) {
      const original = this.#headerElement.buildHeaderMenuData.bind(
        this.#headerElement,
      );
      this.#headerElement.buildHeaderMenuData = () => {
        const sections = original();
        const profileSection = sections.find((section) => section.label === 'Profile');
        if (profileSection) {
          profileSection.label = this.userInfo.name || '';
          profileSection.avatarText = this.userInfo.initials || '';
        }
        return sections;
      };
      this.#headerElement.profileLabelPatched = true;
    }

    const kebab = document.querySelector('neptune-kebab-menu');
    if (kebab) {
      this.#headerElement.applyKebabMenuData(kebab, this.#headerElement.buildHeaderMenuData());
    }
  }

  /**
   * Attaches outside-click and Escape key listeners.
   * @returns {void}
   */
  #attachGlobalListeners() {
    this.#outsideClickHandler = (event) => {
      const path = event.composedPath();
      if (
        path.includes(this) ||
        (this.#anchorElement && path.includes(this.#anchorElement))
      ) {
        return;
      }
      this.close();
    };
    this.#keydownHandler = (event) => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        this.close();
      }
    };
    document.addEventListener('click', this.#outsideClickHandler, true);
    document.addEventListener('keydown', this.#keydownHandler, true);
  }

  /**
   * Removes global outside-click and Escape key listeners.
   * @returns {void}
   */
  #removeGlobalListeners() {
    if (this.#outsideClickHandler) {
      document.removeEventListener('click', this.#outsideClickHandler, true);
    }
    if (this.#keydownHandler) {
      document.removeEventListener('keydown', this.#keydownHandler, true);
    }
    this.#outsideClickHandler = null;
    this.#keydownHandler = null;
  }

}

customElements.define('neptune-profile-menu', ProfileMenu);
export default ProfileMenu;